'use client';

import { CellContext } from '@tanstack/react-table';
import { COMMON_UI_UTILS, UIMarket } from '@drift/common';
import { DataTableColumnDef } from 'src/components/primitives/Table/types';
import type { ParentOrderInfo } from 'src/@types/historyTables';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import TriggerConditionTooltip from 'src/components/Tables/cells/TriggerConditionTooltip';
import { AmountDisplayType } from './TradePageOrderHistoryTable';
import { RelativeDateDisplay } from 'src/components/Tables/RelativeDateDisplay';
import { ActionCell } from './cells/ActionCell';
import { OrderedSizeCell } from './cells/OrderedSizeCell';
import { FilledSizeCell } from './cells/FilledSizeCell';
import { AvgFillPriceCell } from './cells/AvgFillPriceCell';
import { TriggerConditionCell } from './cells/TriggerConditionCell';
import { StatusCell } from './cells/StatusCell';
import { ExpandCell } from './cells/ExpandCell';

export interface OrderHistoryTableMeta {
	expandedRows: Record<string, boolean>;
	onToggleExpand: (orderId: number) => void;
	amountDisplayType: AmountDisplayType;
	displayDatesAsTimestamp: boolean;
}

interface ColumnOptions {
	onToggleDateDisplay: () => void;
	onToggleAmountDisplay: () => void;
	amountDisplayType: AmountDisplayType;
}

const EMPTY_VALUE = <span className="text-text-tertiary">-</span>;

function getMeta(
	context: CellContext<ParentOrderInfo, unknown>
): OrderHistoryTableMeta {
	return context.table.options.meta as OrderHistoryTableMeta;
}

export function getOrderHistoryColumns(
	options: ColumnOptions
): DataTableColumnDef<ParentOrderInfo>[] {
	const { onToggleDateDisplay, onToggleAmountDisplay, amountDisplayType } =
		options;

	return [
		{
			id: 'time',
			accessorKey: 'lastUpdatedTs',
			header: () => (
				<ToggleHeaderCell label="Time" onClick={onToggleDateDisplay} />
			),
			meta: {
				width: { ratio: 9, minPx: 100 },
				bodyCellClassName: 'first:xl:pl-md',
				headerCellClassName: 'first:xl:pl-md',
			},
			cell: (context) => {
				const { displayDatesAsTimestamp } = getMeta(context);
				return (
					<RelativeDateDisplay
						date={
							new Date(context.row.original.lastUpdatedTs.toNumber() * 1000)
						}
						displayAsTimestamp={displayDatesAsTimestamp}
					/>
				);
			},
		},
		{
			id: 'market',
			accessorKey: 'marketIndex',
			header: 'Market',
			meta: { width: { ratio: 9, minPx: 80 } },
			cell: ({ row }) => {
				const uiMarket = new UIMarket(
					row.original.marketIndex,
					row.original.marketType
				);
				const marketName = uiMarket.uiSymbols.baseAssetDisplaySymbol;
				return (
					<>
						{marketName}
						{uiMarket.isSpot ? '-SPOT' : ''}
					</>
				);
			},
		},
		{
			id: 'action',
			accessorKey: 'direction',
			header: 'Action',
			meta: { width: { ratio: 8, minPx: 80 } },
			cell: ({ row }) => <ActionCell order={row.original} />,
		},
		{
			id: 'type',
			accessorKey: 'orderType',
			header: 'Type',
			meta: { width: { ratio: 8, minPx: 80 } },
			cell: ({ row }) => {
				const orderTypeLabel = COMMON_UI_UTILS.getOrderLabelFromOrderDetails(
					row.original
				);
				return orderTypeLabel === 'Oracle Market' ? 'Market' : orderTypeLabel;
			},
		},
		{
			id: 'ordered',
			accessorKey: 'baseAssetAmount',
			header: () => (
				<ToggleHeaderCell
					label={amountDisplayType === 'base' ? 'Order Size' : 'Order Value'}
					onClick={onToggleAmountDisplay}
				/>
			),
			meta: { width: { ratio: 9, minPx: 92 } },
			cell: (context) => {
				const { amountDisplayType } = getMeta(context);
				return (
					<OrderedSizeCell
						order={context.row.original}
						amountDisplayType={amountDisplayType}
					/>
				);
			},
		},
		{
			id: 'filled',
			accessorKey: 'baseAssetAmountFilled',
			header: () => (
				<ToggleHeaderCell
					label={amountDisplayType === 'base' ? 'Filled Size' : 'Filled Value'}
					onClick={onToggleAmountDisplay}
				/>
			),
			meta: { width: { ratio: 9, minPx: 88 } },
			cell: (context) => {
				const { amountDisplayType } = getMeta(context);
				return (
					<FilledSizeCell
						order={context.row.original}
						amountDisplayType={amountDisplayType}
					/>
				);
			},
		},
		{
			id: 'avgFillPrice',
			header: 'Avg Fill Price',
			meta: { width: { ratio: 9, minPx: 80 } },
			cell: ({ row }) => <AvgFillPriceCell order={row.original} />,
		},
		{
			id: 'fee',
			accessorKey: 'cumulativeFee',
			header: 'Fee',
			meta: { width: { ratio: 6, minPx: 50 } },
			cell: ({ row }) => {
				const { cumulativeFee } = row.original;
				return cumulativeFee?.eqZero()
					? EMPTY_VALUE
					: cumulativeFee?.toNotional();
			},
		},
		{
			id: 'reduceOnly',
			accessorKey: 'reduceOnly',
			header: 'Reduce Only',
			meta: { width: { ratio: 8, minPx: 70 } },
			cell: ({ row }) => (row.original.reduceOnly ? 'Yes' : EMPTY_VALUE),
		},
		{
			id: 'postOnly',
			accessorKey: 'postOnly',
			header: 'Post Only',
			meta: { width: { ratio: 8, minPx: 60 } },
			cell: ({ row }) => (row.original.postOnly ? 'Yes' : EMPTY_VALUE),
		},
		{
			id: 'triggerCondition',
			accessorKey: 'triggerPrice',
			header: () => (
				<TriggerConditionTooltip>Trigger Condition</TriggerConditionTooltip>
			),
			meta: { width: { ratio: 10, minPx: 90 } },
			cell: ({ row }) => <TriggerConditionCell order={row.original} />,
		},
		{
			id: 'status',
			accessorKey: 'lastActionStatus',
			header: 'Status',
			meta: {
				width: { ratio: 7, minPx: 60 },
				bodyCellClassName: 'text-right',
				headerCellClassName: 'text-right',
			},
			cell: ({ row }) => <StatusCell order={row.original} />,
		},
		{
			id: 'expand',
			header: '',
			meta: {
				width: { fixedPx: 32 },
				bodyCellClassName: 'p-0 xl:pr-md',
				headerCellClassName: 'p-0 xl:pr-md',
			},

			cell: (context) => {
				const { expandedRows, onToggleExpand } = getMeta(context);
				const isExpanded = expandedRows[context.row.original.orderId] ?? false;
				return (
					<ExpandCell
						orderId={context.row.original.orderId}
						isExpanded={isExpanded}
						onToggleExpand={onToggleExpand}
					/>
				);
			},
		},
	];
}
